import { useState, useEffect } from "react";
import { ShieldAlert, Wallet } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";
import { usd } from "../lib/format";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export default function BotWalletSwitch({ inst, onChanged }) {
  const { user } = useAuth();
  const { account, refreshAccount } = useMode();
  const [confirm, setConfirm] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const lid = inst.id;
  const mode = inst.wallet_mode || "demo";
  const locked = inst.status === "running" || !!inst.performance?.current_trade;
  const liveRunning = mode === "live" && inst.status === "running";
  useEffect(() => {
    if (!liveRunning) return;
    const t = setInterval(() => refreshAccount?.(), 10000);
    return () => clearInterval(t);
  }, [liveRunning, refreshAccount]);
  const liveBlock = inst.bot?.live_global_enabled === false ? "Live bot trading is currently turned off by the platform" : !inst.bot?.live_available ? "Live trading isn't enabled for this bot yet" : user?.kyc_status !== "approved" ? "Complete KYC verification to trade live" : "";

  const switchTo = async (wm) => {
    setBusy(true);
    try {
      await api.post(`/bots/instances/${lid}/wallet-mode`, { wallet_mode: wm, confirm_live: wm === "live" });
      toast.success(wm === "live" ? "Bot will now trade on your LIVE wallet" : "Bot switched to your demo wallet");
      setConfirm(false); setAck(false); onChanged?.(); refreshAccount?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not switch wallet"); }
    setBusy(false);
  };
  const pick = (wm) => {
    if (wm === mode || locked || busy) return;
    if (wm === "live") { if (liveBlock) return toast.error(liveBlock); setConfirm(true); } else switchTo("demo");
  };

  return (
    <div className="mt-3" data-testid={`bot-wallet-switch-${lid}`}>
      <div className="flex items-center gap-2">
        <Wallet className="h-3.5 w-3.5 text-fx-s3" />
        <span className="text-xs text-fx-s3">Trading wallet</span>
        <div className="ml-auto inline-flex rounded-lg bg-fx-base border border-white/10 p-0.5">
          {["demo", "live"].map((wm) => (
            <button key={wm} onClick={() => pick(wm)} disabled={locked && wm !== mode} data-testid={`bot-wallet-${wm}-${lid}`}
              title={locked ? "Stop the bot and wait for open trades to close to switch" : wm === "live" ? liveBlock : ""}
              className={clsx("px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wide transition-colors disabled:cursor-not-allowed",
                mode === wm ? (wm === "live" ? "bg-neg text-white" : "bg-brandblue text-white") : "text-fx-s3 hover:text-white",
                wm === "live" && liveBlock && mode !== "live" && "opacity-50")}>{wm}</button>
          ))}
        </div>
      </div>
      {mode === "live" && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-neg/30 bg-neg/10 px-2.5 py-1.5 text-[11px] text-neg" data-testid={`bot-live-banner-${lid}`}>
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> <span>Live balance <b className="font-mono" data-testid={`bot-live-balance-${lid}`}>{usd(account?.live_wallet?.balance ?? 0)}</b> · Trading with real funds. Stakes and profits/losses use your live balance. The bot stops automatically if your balance drops below the stake.</span>
        </div>
      )}
      <Dialog open={confirm} onOpenChange={(o) => { setConfirm(o); if (!o) setAck(false); }}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-md" data-testid="bot-live-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-neg" /> Run {inst.bot?.name} on your LIVE wallet?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-fx-s2">
            <p>The bot will place trades with <b className="text-white">real money</b> from your live balance. Each trade stakes <b className="text-white">${inst.trade_amount}</b>. Trading carries risk, and you can lose some or all of your funds.</p>
            <p className="text-xs text-fx-s3">The bot stops automatically if your live balance falls below the stake. You can pause or stop it at any time.</p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="bot-live-ack" className="mt-0.5" />
              <span>I understand the risks and want this bot to trade with my real funds.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} className="border-white/10 bg-fx-base">Cancel</Button>
            <Button onClick={() => switchTo("live")} disabled={!ack || busy} data-testid="bot-live-confirm" className="bg-neg hover:bg-red-600">Switch to Live</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
